import React, { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { FaGithub, FaLock, FaSearch, FaExternalLinkAlt, FaGooglePlay, FaYoutube } from 'react-icons/fa';
import { inhouseProjects } from '../data/inhouseProjects';
import { featuredProjects } from '../data/featuredProjects';

const PAGE_SIZE = 12;

// One merged list: featured projects (demo videos / Play Store) first, then the rest of the repo-backed projects.
// A featured entry with `mergeWith` enriches the matching repo project instead of duplicating it.
const allProjects = (() => {
  const byId = new Map(inhouseProjects.map((p) => [p.id, p]));
  const featured = featuredProjects.map((f) => {
    const base = f.mergeWith ? byId.get(f.mergeWith) : null;
    if (base) {
      byId.delete(f.mergeWith);
      return { ...base, ...f, repo: base.repo, private: base.private, featured: true };
    }
    return { ...f, featured: true };
  });
  return [...featured, ...byId.values()];
})();

const linkIcon = (type) => (type === 'play' ? <FaGooglePlay /> : <FaYoutube />);

const AllProjects = ({ standalone = false }) => {
  const [category, setCategory] = useState('All');
  const [search, setSearch] = useState('');
  const [visible, setVisible] = useState(PAGE_SIZE);

  const categories = useMemo(() => {
    const counts = {};
    allProjects.forEach((p) => { counts[p.category] = (counts[p.category] || 0) + 1; });
    return [['All', allProjects.length], ...Object.entries(counts).sort((a, b) => b[1] - a[1])];
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return allProjects.filter(
      (p) =>
        (category === 'All' || p.category === category) &&
        (!q ||
          p.title.toLowerCase().includes(q) ||
          p.description.toLowerCase().includes(q) ||
          p.tags.join(' ').toLowerCase().includes(q))
    );
  }, [category, search]);

  return (
    <section id="all-projects" className={`relative bg-[#061a10] pb-24 px-4 overflow-hidden ${standalone ? 'pt-36' : 'pt-24'}`}>
      <div className="absolute top-0 right-0 w-[40rem] h-[40rem] bg-accent-green/10 rounded-full blur-[160px] pointer-events-none" />
      <div className="relative z-10 container mx-auto max-w-7xl">
        {/* Top actions: Tech Solutions (events & expo activities) + direct link to the full-page gallery */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mb-10">
          <a
            href="https://solutions.platypusbox.com"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-accent-green text-forest font-semibold px-6 py-3 rounded-md hover:bg-accent-green/90 transition-colors shadow-lg shadow-accent-green/20"
          >
            Tech Solutions: Event &amp; Expo Activities
            <FaExternalLinkAlt className="text-xs" />
          </a>
          {!standalone && (
            <a
              href="/projects"
              className="inline-flex items-center gap-2 border border-accent-green/60 text-cream px-6 py-3 rounded-md hover:bg-accent-green/10 transition-colors"
            >
              Open as full page
            </a>
          )}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <span className="inline-block text-accent-green tracking-[0.3em] text-xs md:text-sm font-semibold uppercase mb-4">
            Our Work
          </span>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-cream mb-5">
            Our <span className="text-accent-green">Projects</span>
          </h2>
          <p className="text-cream/80 text-lg max-w-3xl mx-auto">
            {allProjects.length} in-house builds across games, AR/VR, AI, business apps and hardware.
            Featured demos come first. Cards link to demo videos, Play Store pages and source repositories.
          </p>
        </motion.div>

        <div className="flex flex-col md:flex-row md:items-center gap-4 mb-10">
          <div className="flex flex-wrap gap-2 flex-1">
            {categories.map(([name, count]) => (
              <button
                key={name}
                onClick={() => { setCategory(name); setVisible(PAGE_SIZE); }}
                className={`text-sm px-4 py-1.5 rounded-full border transition-colors ${
                  category === name
                    ? 'bg-accent-green text-forest border-accent-green font-semibold'
                    : 'text-cream/80 border-cream/20 hover:border-accent-green/60'
                }`}
              >
                {name} <span className="opacity-60">({count})</span>
              </button>
            ))}
          </div>
          <label className="relative md:w-72">
            <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-cream/40 text-sm" />
            <input
              type="text"
              value={search}
              onChange={(e) => { setSearch(e.target.value); setVisible(PAGE_SIZE); }}
              placeholder="Search projects..."
              className="w-full pl-9 pr-3 py-2 rounded bg-forest text-cream placeholder-cream/40 focus:outline-none focus:ring focus:ring-accent-green"
            />
          </label>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {filtered.slice(0, visible).map((p) => (
            <motion.article
              key={p.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.4 }}
              className="group flex flex-col bg-forest/70 border border-accent-green/20 hover:border-accent-green/60 rounded-xl overflow-hidden transition-colors"
            >
              <div className="relative aspect-video overflow-hidden bg-black">
                <img
                  src={p.image}
                  alt={`${p.title} artwork`}
                  loading="lazy"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <span className="absolute top-2 left-2 text-[10px] uppercase tracking-wider bg-black/60 text-cream px-2 py-1 rounded-full">
                  {p.category}
                </span>
                {p.featured && (
                  <span className="absolute top-2 right-2 text-[10px] uppercase tracking-wider bg-accent-green text-forest font-bold px-2 py-1 rounded-full">
                    Featured
                  </span>
                )}
              </div>
              <div className="p-5 flex flex-col flex-1">
                <h3 className="text-lg font-bold text-cream mb-1">{p.title}</h3>
                <p className="text-accent-green text-sm italic mb-2">{p.tagline}</p>
                <p className="text-cream/70 text-sm mb-4 flex-1">{p.description}</p>
                <div className="flex flex-wrap gap-1.5 mb-4">
                  {p.tags.map((t) => (
                    <span key={t} className="text-[11px] text-cream/70 border border-cream/15 rounded-full px-2 py-0.5">
                      {t}
                    </span>
                  ))}
                </div>
                <div className="border-t border-cream/10 pt-3 flex flex-col gap-2">
                  {(p.links || []).map((l) => (
                    <a
                      key={l.url}
                      href={l.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 text-sm text-cream hover:text-accent-green transition-colors"
                    >
                      {linkIcon(l.type)} {l.label}
                    </a>
                  ))}
                  {p.repo && (
                    <a
                      href={p.repo}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center justify-between text-sm text-cream hover:text-accent-green transition-colors"
                    >
                      <span className="inline-flex items-center gap-2">
                        <FaGithub /> View repository
                      </span>
                      {p.private && (
                        <span className="inline-flex items-center gap-1 text-[11px] text-cream/50">
                          <FaLock /> Private
                        </span>
                      )}
                    </a>
                  )}
                </div>
              </div>
            </motion.article>
          ))}
        </div>

        {filtered.length === 0 && <p className="text-center text-cream/60 mt-10">No projects match your search.</p>}

        {visible < filtered.length && (
          <div className="text-center mt-12">
            <button
              onClick={() => setVisible((v) => v + PAGE_SIZE)}
              className="bg-accent-green text-forest font-semibold px-8 py-3 rounded-md hover:bg-accent-green/90 transition-colors"
            >
              Show more ({filtered.length - visible} left)
            </button>
          </div>
        )}
      </div>
    </section>
  );
};

export default AllProjects;
